import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database'

/* Removed logging of Supabase URL and Key for security */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Auth helpers
export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  } catch (err) {
    return { data: null, error: { message: 'Authentication service unavailable. Please try again.' } }
  }
}

export const signUp = async (email: string, password: string, userData?: any) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: userData
    }
  })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  return { user, error }
}

export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession()
  return { session, error }
}

export const resetPassword = async (email: string) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`
  })
  return { data, error }
}

// Database helpers
export const getTyres = async (filters?: any) => {
  try {
    let query = supabase
      .from('tyre_inventory')
      .select(`
        *,
        tyre_spec:tyre_specifications(*),
        business:businesses(*)
      `)
      .gt('quantity', 0)
      .eq('business.business_status', 'active') // Only show tyres from active businesses

    if (filters?.size) {
      // Search by size in tyre_inventory.size column (set by shop owners from central DB)
      query = query.eq('size', filters.size)
    }

    if (filters?.brand) {
      query = query.ilike('brand', `%${filters.brand}%`)
    }

    if (filters?.maxPrice) {
      query = query.lte('price', filters.maxPrice)
    }

    const { data, error } = await query.order('price', { ascending: true })
    
    // If database query fails, return demo data
    if (error) {
      console.warn('Database not available, using demo data:', error.message)
      const { DEMO_TYRES } = await import('../stores/tyreStore')
      
      let filteredData = DEMO_TYRES
      
      if (filters?.size) {
        filteredData = filteredData.filter(tyre => tyre.tyre_spec.size_code === filters.size)
      }
      
      if (filters?.brand) {
        filteredData = filteredData.filter(tyre =>
          tyre.brand.toLowerCase().includes(filters.brand.toLowerCase())
        )
      }
      
      if (filters?.maxPrice) {
        filteredData = filteredData.filter(tyre => tyre.price <= filters.maxPrice)
      }
      
      return { data: filteredData, error: null }
    }
    
    return { data, error }
  } catch (err) {
    console.warn('Database connection failed, using demo data:', err)
    const { DEMO_TYRES } = await import('../stores/tyreStore')
    return { data: DEMO_TYRES, error: null }
  }
}

export const getBusinesses = async (location?: { lat: number; lng: number }, radius?: number) => {
  let query = supabase
    .from('businesses')
    .select('*')
    .eq('business_status', 'active') // Only show active businesses to customers

  if (location && radius) {
    // This would require PostGIS functions for geographic queries
    // For now, we'll return all businesses
  }

  const { data, error } = await query.order('name')
  return { data, error }
}

export const createBooking = async (bookingData: any) => {
  const { data, error } = await supabase
    .from('bookings')
    .insert(bookingData)
    .select()

  return { data, error }
}

export const getUserBookings = async (userId: string) => {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      business:businesses(*),
      tyre_inventory(*)
    `)
    .eq('customer_id', userId)
    .order('appointment_date', { ascending: false })

  return { data, error }
}

export const getBusinessBookings = async (businessId: string) => {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      customer:user_profiles(*)
    `)
    .eq('business_id', businessId)
    .order('appointment_date', { ascending: true })

  return { data, error }
}

// Real-time subscriptions
export const subscribeToBookings = (businessId: string, callback: (payload: any) => void) => {
  return supabase
    .channel('bookings')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'bookings',
        filter: `business_id=eq.${businessId}`
      },
      callback
    )
    .subscribe()
}

export const subscribeToInventory = (businessId: string, callback: (payload: any) => void) => {
  return supabase
    .channel('inventory')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'tyre_inventory',
        filter: `business_id=eq.${businessId}`
      },
      callback
    )
    .subscribe()
}